import React, { useState, useEffect, useCallback } from 'react';
import {
  RiCheckboxCircleLine,
  RiCheckboxBlankCircleLine,
  RiArrowDownSLine,
  RiArrowRightSLine,
  RiFileList3Line,
  RiLoader4Line,
} from 'react-icons/ri';
import {
  UNIVERSAL_CHECKLIST,
  VISA_SPECIFIC_CHECKLISTS,
  ChecklistItem,
} from '@/utils/intakeChecklists';
import Card from '@/components/ui/Card';
import { ChecklistsApi, Checklist, ChecklistItem as ApiChecklistItem } from '@/api/checklists';

interface IntakeChecklistProps {
  caseId: string;
  visaType?: string;
}

const ChecklistSection: React.FC<{
  title: string;
  items: ChecklistItem[];
  checklist: Checklist | null;
  caseId: string;
  onToggle: () => void;
  defaultExpanded?: boolean;
}> = ({ title, items, checklist, caseId, onToggle, defaultExpanded = false }) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [toggling, setToggling] = useState<string | null>(null);

  const toggleItem = async (document: string) => {
    if (!checklist || toggling) return;

    const checklistItem = checklist.items.find(
      (item) => item.description === document
    );

    if (!checklistItem) return;

    setToggling(checklistItem.id);
    try {
      await ChecklistsApi.toggleChecklistItem(
        caseId,
        checklist.id,
        checklistItem.id
      );
      onToggle();
    } catch (error) {
      console.error('Failed to toggle checklist item:', error);
    } finally {
      setToggling(null);
    }
  };

  const completedCount = checklist
    ? checklist.items.filter((item) => item.isCompleted).length
    : 0;
  const progress = checklist ? Math.round((completedCount / checklist.items.length) * 100) : 0;

  return (
    <div className="border border-neutral-200 rounded-lg overflow-hidden mb-4">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-4 bg-neutral-50 hover:bg-neutral-100 transition-colors"
      >
        <div className="flex items-center gap-3">
          {isExpanded ? (
            <RiArrowDownSLine className="w-5 h-5 text-neutral-500" />
          ) : (
            <RiArrowRightSLine className="w-5 h-5 text-neutral-500" />
          )}
          <div className="text-left">
            <h3 className="font-medium text-neutral-900">{title}</h3>
            <p className="text-xs text-neutral-500">
              {completedCount} of {checklist?.items.length || items.length} items collected
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="w-24 h-2 bg-neutral-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-green-500 transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>
          <span className="text-xs font-medium text-neutral-600 w-8 text-right">
            {progress}%
          </span>
        </div>
      </button>

      {isExpanded && (
        <div className="bg-white divide-y divide-neutral-100">
          {items.map((item) => {
            const checklistItem = checklist?.items.find(
              (ci) => ci.description === item.document
            );
            const isChecked = checklistItem?.isCompleted || false;
            const isTogglingThisItem = toggling === checklistItem?.id;

            return (
              <div
                key={item.document}
                className={`flex items-start gap-3 p-3 transition-colors ${
                  isTogglingThisItem ? 'opacity-50' : 'cursor-pointer hover:bg-neutral-50'
                } ${isChecked ? 'bg-green-50/30' : ''}`}
                onClick={() => !isTogglingThisItem && toggleItem(item.document)}
              >
                <div className="mt-0.5 flex-shrink-0 w-5 h-5">
                  {isTogglingThisItem ? (
                    <RiLoader4Line className="w-5 h-5 text-neutral-400 animate-spin" />
                  ) : isChecked ? (
                    <RiCheckboxCircleLine className="w-5 h-5 text-green-600" />
                  ) : (
                    <RiCheckboxBlankCircleLine className="w-5 h-5 text-neutral-400 hover:text-neutral-600" />
                  )}
                </div>
                <div>
                  <p
                    className={`text-sm font-medium ${
                      isChecked ? 'text-neutral-500 line-through' : 'text-neutral-900'
                    }`}
                  >
                    {item.document}
                  </p>
                  {item.notes && (
                    <p className="text-xs text-neutral-500 mt-0.5">{item.notes}</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const IntakeChecklist: React.FC<IntakeChecklistProps> = ({ caseId, visaType }) => {
  const visaChecklist = visaType ? VISA_SPECIFIC_CHECKLISTS[visaType] : null;
  const [checklists, setChecklists] = useState<Record<string, Checklist>>({});
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadChecklists = useCallback(async (isInitial = false) => {
    try {
      if (isInitial) setInitialLoading(true);
      const response = await ChecklistsApi.getChecklists(caseId);

      if (response.success) {
        const checklistMap: Record<string, Checklist> = {};
        response.checklists.forEach((checklist) => {
          checklistMap[checklist.name] = checklist;
        });
        setChecklists(checklistMap);

        // Initialize checklists if they don't exist
        if (!checklistMap[UNIVERSAL_CHECKLIST.title]) {
          await ChecklistsApi.createChecklist(caseId, {
            name: UNIVERSAL_CHECKLIST.title,
            items: UNIVERSAL_CHECKLIST.items.map((item, index) => ({
              document: item.document,
              notes: item.notes,
              isRequired: false,
              sortOrder: index,
            })),
          });
        }

        if (visaChecklist && !checklistMap[visaChecklist.title]) {
          await ChecklistsApi.createChecklist(caseId, {
            name: visaChecklist.title,
            items: visaChecklist.items.map((item, index) => ({
              document: item.document,
              notes: item.notes,
              isRequired: false,
              sortOrder: index,
            })),
          });
        }

        // Reload after initialization
        const updatedResponse = await ChecklistsApi.getChecklists(caseId);
        if (updatedResponse.success) {
          const updatedMap: Record<string, Checklist> = {};
          updatedResponse.checklists.forEach((checklist) => {
            updatedMap[checklist.name] = checklist;
          });
          setChecklists(updatedMap);
        }
      }
    } catch (err) {
      console.error('Failed to load checklists:', err);
      setError('Failed to load checklists');
    } finally {
      setInitialLoading(false);
    }
  }, [caseId, visaChecklist]);

  useEffect(() => {
    loadChecklists(true);
  }, [loadChecklists]);

  // Auto-refresh checklists every 10 seconds while documents are processing
  // This ensures checklist items auto-complete when worker finishes
  useEffect(() => {
    const interval = setInterval(() => {
      loadChecklists(false); // Silent refresh without loading state
    }, 10000); // 10 second polling

    return () => clearInterval(interval);
  }, [loadChecklists]);

  if (initialLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <RiLoader4Line className="w-6 h-6 text-primary-600 animate-spin" />
        <span className="ml-2 text-neutral-600">Loading checklist...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-red-600 text-sm p-4 bg-red-50 rounded-lg">
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 mb-2">
        <RiFileList3Line className="w-5 h-5 text-primary-600" />
        <h2 className="text-lg font-semibold text-neutral-900">
          Intake Checklist
        </h2>
      </div>

      <ChecklistSection
        title={UNIVERSAL_CHECKLIST.title}
        items={UNIVERSAL_CHECKLIST.items}
        checklist={checklists[UNIVERSAL_CHECKLIST.title] || null}
        caseId={caseId}
        onToggle={loadChecklists}
        defaultExpanded={false}
      />

      {visaChecklist && (
        <ChecklistSection
          title={visaChecklist.title}
          items={visaChecklist.items}
          checklist={checklists[visaChecklist.title] || null}
          caseId={caseId}
          onToggle={loadChecklists}
          defaultExpanded={false}
        />
      )}
    </div>
  );
};

export default IntakeChecklist;
